import React, {useCallback, useState} from 'react';
import type {
  DragEndEvent,
  DragStartEvent} from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {SyntheticListenerMap} from '@dnd-kit/core/dist/hooks/utilities';
import type {UniqueIdentifier} from '@dnd-kit/core/dist/types/other';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {DndSortableItem} from './DndSortableItem';

type Props<Item extends {id: UniqueIdentifier}> = {
  id?: string;
  items: Item[];
  renderer: (
    id: UniqueIdentifier,
    listeners: SyntheticListenerMap | undefined,
  ) => React.JSX.Element;
  onMove: (from: number, to: number) => void;
};

export function DndContainer<Item extends {id: UniqueIdentifier}>({
  id,
  items,
  renderer,
  onMove,
}: Props<Item>) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 50,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event;
      const activeId = active.id;
      const overId = over?.id;

      const activeIndex = items.findIndex(i => i.id === activeId);
      const overIndex = items.findIndex(i => i.id === overId);

      setActiveId(null);
      if (activeIndex !== overIndex) {
        onMove(activeIndex, overIndex);
      }
    },
    [items, onMove],
  );

  return (
    <DndContext
      id={id}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map(item => (
          <DndSortableItem
            key={item.id}
            id={item.id}
            isActive={item.id === activeId}
            renderer={renderer}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
